import {
  AlarmClock,
  Building2,
  CalendarDays,
  Handshake,
  type LucideIcon,
} from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { useMemo, type ReactNode } from "react";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";

import { formatCurrency } from "../customFields/format";
import { addDays, startOfDay } from "../events/calendarUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";

const UPCOMING_DAYS = 30;
const NEW_PROSPECT_DAYS = 30;
// Enough to sum a small venue's whole open pipeline in one request.
const MAX_OPEN_DEALS = 1000;

/** Key figures of the venue's commercial activity, each linking to its page. */
export const DashboardKpis = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { currency } = useConfigurationContext();
  const { identity } = useGetIdentity();
  // Computed once per page, so the query keys stay stable.
  const dates = useMemo(() => {
    const today = startOfDay(new Date());
    return {
      today: today.toISOString(),
      upcomingEnd: addDays(today, UPCOMING_DAYS).toISOString(),
      newSince: addDays(today, -NEW_PROSPECT_DAYS).toISOString(),
    };
  }, []);

  const { data: openDeals = [], isPending: dealsPending } = useGetList<Deal>(
    "deals",
    {
      filter: { "status@in": "(open,late)", "archived_at@is": null },
      pagination: { page: 1, perPage: MAX_OPEN_DEALS },
    },
  );
  const { total: upcomingEvents } = useGetList("events", {
    filter: {
      "start_at@gte": dates.today,
      "start_at@lt": dates.upcomingEnd,
    },
    pagination: { page: 1, perPage: 1 },
  });
  const { total: lateTasks } = useGetList(
    "tasks",
    {
      filter: {
        status: "todo",
        "due_date@lt": dates.today,
        sales_id: identity?.id,
      },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: identity?.id != null },
  );
  const { total: newProspects } = useGetList("companies", {
    filter: { "created_at@gte": dates.newSince },
    pagination: { page: 1, perPage: 1 },
  });

  const pipelineAmount = openDeals.reduce(
    (sum, deal) => sum + (deal.amount ?? 0),
    0,
  );

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Kpi
        to="/deals"
        Icon={Handshake}
        label={translate("crm.dashboard.kpis.open_deals")}
        value={dealsPending ? undefined : openDeals.length}
        detail={
          dealsPending
            ? undefined
            : formatCurrency(pipelineAmount, locale, currency)
        }
      />
      <Kpi
        to="/events"
        Icon={CalendarDays}
        label={translate("crm.dashboard.kpis.upcoming_events")}
        value={upcomingEvents}
        detail={translate("crm.dashboard.kpis.next_days", {
          smart_count: UPCOMING_DAYS,
        })}
      />
      <Kpi
        to="/tasks"
        Icon={AlarmClock}
        label={translate("crm.dashboard.kpis.late_tasks")}
        value={lateTasks}
        detail={translate("crm.dashboard.kpis.mine")}
        isAlert={!!lateTasks}
      />
      <Kpi
        to="/companies"
        Icon={Building2}
        label={translate("crm.dashboard.kpis.new_prospects")}
        value={newProspects}
        detail={translate("crm.dashboard.kpis.last_days", {
          smart_count: NEW_PROSPECT_DAYS,
        })}
      />
    </div>
  );
};

const Kpi = ({
  to,
  Icon,
  label,
  value,
  detail,
  isAlert,
}: {
  to: string;
  Icon: LucideIcon;
  label: string;
  value?: number;
  detail?: ReactNode;
  isAlert?: boolean;
}) => (
  <Link to={to} className="group">
    <Card className="p-4 gap-1 h-full transition-colors group-hover:border-primary/40">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        {label}
        <Icon className="size-4" />
      </div>
      <div
        className={`font-heading text-3xl font-semibold tabular-nums ${isAlert ? "text-destructive" : ""}`}
      >
        {value ?? "–"}
      </div>
      {detail ? (
        <div className="text-xs text-muted-foreground">{detail}</div>
      ) : null}
    </Card>
  </Link>
);
